// Calcul des notes côté client.
//
// Reproduit `backend/app/notation.py` pour que les totaux se recalculent au
// clavier sans aller-retour serveur. Le serveur reste la référence : c'est
// lui qui alimente les statistiques et les documents produits.

import type { Copie, Question } from "./api";
// Extension explicite : `node --test` résout en ESM, qui l'exige. Vite
// l'accepte tout autant, et c'est à ce prix que ce module est testable
// sans installer un exécuteur de tests.
import { niveauPourTaux, type NiveauMaitrise } from "./palette.ts";

export const BAREME_MAX = 4;

/** Points obtenus sur une question : (note / 4) × poids. Non saisie = 0. */
export function pointsQuestion(valeur: number | undefined, poids: number): number {
  return valeur === undefined ? 0 : (valeur / BAREME_MAX) * poids;
}

export type TotauxCopie = {
  parExercice: Record<number, number>;
  noteTotale: number;
  noteBonus: number;
  noteFinale: number;
  saisies: number;
};

export function totauxCopie(copie: Copie, questions: Question[]): TotauxCopie {
  const parExercice: Record<number, number> = {};
  let noteTotale = 0;
  let noteBonus = 0;

  for (const question of questions) {
    const points = pointsQuestion(copie.notes[question.ref], question.poids);
    if (question.bonus) {
      noteBonus += points;
    } else {
      noteTotale += points;
      parExercice[question.exercice] = (parExercice[question.exercice] ?? 0) + points;
    }
  }

  return {
    parExercice,
    noteTotale,
    noteBonus,
    // Jamais plafonnée : dépasser 20 grâce au bonus est voulu.
    noteFinale: noteTotale + noteBonus,
    saisies: Object.keys(copie.notes).length,
  };
}

/** Taux de réussite d'une compétence pour une copie, entre 0 et 1. */
export function tauxCompetence(
  copie: Copie,
  questions: Question[],
  code: string,
): { taux: number; niveau: NiveauMaitrise } | null {
  const concernees = questions.filter((q) => q.competences.includes(code));
  if (concernees.length === 0) return null;

  const obtenu = concernees.reduce(
    (somme, q) => somme + (copie.notes[q.ref] ?? 0) * q.poids,
    0,
  );
  const possible = concernees.reduce((somme, q) => somme + BAREME_MAX * q.poids, 0);
  const taux = possible === 0 ? 0 : obtenu / possible;
  return { taux, niveau: niveauPourTaux(taux) };
}

/** Arrondi d'affichage : deux décimales, sans zéros inutiles. */
export function afficherNote(valeur: number): string {
  return Number(valeur.toFixed(2)).toLocaleString("fr-FR");
}
